import React, { useMemo } from "react";

import { Module } from "src/api/modulesApi";
import { Button } from "src/base-components/Button";
import { Pill } from "src/base-components/Pill";
import { PALETTE } from "src/design-system/utils/colorPalette";

export type VersionFilter =
  | { version: string; source: "desired" | "current" }
  | { source: "not_deployed" }
  | null;

type Props = {
  modules: Module[];
  selectedFilter: VersionFilter;
  onSelectFilter: (filter: VersionFilter) => void;
};

type VersionCounts = Record<string, number>;

const countVersions = (
  modules: Module[],
  getVersion: (mod: Module) => string | null,
): VersionCounts => {
  const counts: VersionCounts = {};
  modules.map((mod) => {
    const version = getVersion(mod);
    if (version) {
      counts[version] = (counts[version] ?? 0) + 1;
    }
    return null;
  });
  return counts;
};

type VersionBarProps = {
  label: string;
  source: "desired" | "current";
  counts: VersionCounts;
  total: number;
  colorMap: Map<string, string>;
  selectedFilter: VersionFilter;
  onSelectFilter: (filter: VersionFilter) => void;
  nullCount?: number;
};

const VersionBar: React.FC<VersionBarProps> = ({
  label,
  source,
  counts,
  total,
  colorMap,
  selectedFilter,
  onSelectFilter,
  nullCount = 0,
}) => {
  const versions = Object.keys(counts).sort();
  const isThisSource = selectedFilter?.source === source;

  const handleClick = (version: string) => {
    if (isThisSource && selectedFilter?.version === version) {
      onSelectFilter(null);
    } else {
      onSelectFilter({ version, source });
    }
  };

  const isNotDeployedSelected = selectedFilter?.source === "not_deployed";

  return (
    <div>
      <div className="mb-1.5 font-inter-medium-12px text-gray-700">{label}</div>
      <div className="flex h-5 w-full overflow-hidden rounded border border-gray-200">
        {versions.map((version) => {
          const count = counts[version];
          const widthPercent = (count / total) * 100;
          const isSelected =
            isThisSource && selectedFilter?.version === version;
          return (
            <button
              key={version}
              className="h-full cursor-pointer transition-all duration-300 focus:outline-none"
              style={{
                width: `${widthPercent}%`,
                background: colorMap.get(version) ?? "#d1d5db",
                outline: isSelected ? "2px solid #222" : undefined,
                outlineOffset: "-2px",
              }}
              title={`${version}: ${count} module${count !== 1 ? "s" : ""}`}
              type="button"
              onClick={() => handleClick(version)}
            />
          );
        })}
        {nullCount > 0 && (
          <button
            className="h-full cursor-pointer bg-gray-200 focus:outline-none"
            style={{
              width: `${(nullCount / total) * 100}%`,
              backgroundImage:
                "repeating-linear-gradient(135deg, transparent, transparent 3px, rgba(0,0,0,0.08) 3px, rgba(0,0,0,0.08) 6px)",
              outline: isNotDeployedSelected ? "2px solid #222" : undefined,
              outlineOffset: "-2px",
            }}
            title={`Not deployed: ${nullCount} module${nullCount !== 1 ? "s" : ""}`}
            type="button"
            onClick={() =>
              onSelectFilter(
                isNotDeployedSelected ? null : { source: "not_deployed" },
              )
            }
          />
        )}
      </div>
      <div className="mt-1.5 flex flex-wrap items-center gap-2">
        {versions.map((version) => {
          const count = counts[version];
          const isSelected =
            isThisSource && selectedFilter?.version === version;
          return (
            <Pill
              key={version}
              size="base"
              variant={isSelected ? "indigo-light" : "white"}
              onClick={() => handleClick(version)}
            >
              <span
                className="inline-block h-2.5 w-2.5 shrink-0 rounded"
                style={{ background: colorMap.get(version) }}
              />
              <span className="ml-0.5 font-code-12">{version}</span>
              <span className="font-inter-normal-12px text-gray-400">
                ({count})
              </span>
            </Pill>
          );
        })}
        {nullCount > 0 && (
          <Pill
            size="base"
            variant={isNotDeployedSelected ? "indigo-light" : "white"}
            onClick={() =>
              onSelectFilter(
                isNotDeployedSelected ? null : { source: "not_deployed" },
              )
            }
          >
            <span
              className="inline-block h-2.5 w-2.5 shrink-0 rounded border border-gray-300 bg-gray-200"
              style={{
                backgroundImage:
                  "repeating-linear-gradient(135deg, transparent, transparent 2px, rgba(0,0,0,0.08) 2px, rgba(0,0,0,0.08) 4px)",
              }}
            />
            <span className="ml-0.5 font-code-12 text-gray-500">
              Not deployed
            </span>
            <span className="font-inter-normal-12px text-gray-400">
              ({nullCount})
            </span>
          </Pill>
        )}
      </div>
    </div>
  );
};

export const ModuleVersionBars: React.FC<Props> = ({
  modules,
  selectedFilter,
  onSelectFilter,
}) => {
  const { deployedCounts, desiredCounts, colorMap, nullDeployedCount } =
    useMemo(() => {
      const deployed = countVersions(
        modules,
        (mod) => mod.spec?.version ?? null,
      );
      const desired = countVersions(modules, (mod) => mod.desiredSpec.version);

      const allVersions = [
        ...new Set([...Object.keys(deployed), ...Object.keys(desired)]),
      ].sort();

      const cMap = new Map(
        allVersions.map((version, i) => [version, PALETTE[i % PALETTE.length]]),
      );

      const nullCount = modules.filter((mod) => mod.spec === null).length;

      return {
        deployedCounts: deployed,
        desiredCounts: desired,
        colorMap: cMap,
        nullDeployedCount: nullCount,
      };
    }, [modules]);

  if (modules.length === 0) {
    return null;
  }

  const total = modules.length;

  return (
    <div className="mb-4 rounded-lg border border-gray-200 p-4">
      <div className="flex items-center justify-between">
        <h3 className="mb-3 font-inter-semibold-14px text-gray-900">
          Version overview
        </h3>
        {selectedFilter && (
          <Button
            size="sm"
            variant="tertiary"
            onClick={() => onSelectFilter(null)}
          >
            Clear filter
          </Button>
        )}
      </div>
      <div className="space-y-4">
        <VersionBar
          colorMap={colorMap}
          counts={desiredCounts}
          label="Desired Versions"
          selectedFilter={selectedFilter}
          source="desired"
          total={total}
          onSelectFilter={onSelectFilter}
        />
        <VersionBar
          colorMap={colorMap}
          counts={deployedCounts}
          label="Current Versions"
          nullCount={nullDeployedCount}
          selectedFilter={selectedFilter}
          source="current"
          total={total}
          onSelectFilter={onSelectFilter}
        />
      </div>
    </div>
  );
};
